<script setup lang="ts">
import { useAddressSystemStore } from '@/store/addressSystem'
import { useBasketStore } from '@/store/basket/basket'
import { useBasketStateStore } from '@/store/basket/basket-state'
import { useUserStore } from '@/store/user/user'

import { SetDeliveryMethodPayload } from '@/api/modules/basket/types'
import type { AddressWidgets } from '@/types/address-system'
import { BasketStep } from '@/types/basket'

import { storage } from '@/services/storage'
import { STORAGE_KEY } from '@/services/storage/constants'

const TITLE_NO_ADDRESS = 'Куда заказать?'

const STATE_DELIVERY_WIDGET: AddressWidgets = {
  unset: {
    type: 'unset',
    icon: 'location--filled',
    color: 'var(--ui-primary)',
  },
  courier: {
    type: 'courier',
    icon: 'truck-delivery',
    color: '',
  },
  hall: {
    type: 'hall',
    icon: 'dish',
    color: '',
  },
  pickup: {
    type: 'pickup',
    icon: 'shopping-bag',
    color: '',
  },
}

const addressSystemStore = useAddressSystemStore()
const basketStore = useBasketStore()
const basketStateStore = useBasketStateStore()
const userStore = useUserStore()

const isAuth = computed(() => userStore.isAuthenticated)

const isLoadingDeliveryWidget = computed(
  () => addressSystemStore.isLoadingWidget,
)

const currentDeliveryMethod = computed(() => {
  return isAuth.value
    ? basketStore.data.delivery.method
    : addressSystemStore.deliveryWidget.delivery_method
})

const currentBasketAddress = computed(() => {
  return isAuth.value
    ? basketStore.data.delivery.order_address
    : addressSystemStore.deliveryWidget.order_address
})

// Состояние виджета
const currentDeliveryWidget = computed(() => {
  if (!isAuth.value && !addressSystemStore.deliveryWidget.order_address) {
    return STATE_DELIVERY_WIDGET.unset
  }

  return !currentDeliveryMethod.value || !currentBasketAddress.value
    ? STATE_DELIVERY_WIDGET.unset
    : STATE_DELIVERY_WIDGET[currentDeliveryMethod.value]
})

const isAddressUnset = computed(
  () => currentDeliveryWidget.value.type === 'unset',
)

const isBasketSecondStep = computed(() => {
  return basketStateStore.step === BasketStep.SECOND
})

const openDialogAddress = () => {
  if (isBasketSecondStep.value) return

  addressSystemStore.setVisibleAddressSystemDialog(true)
}

// Устанавливаем начальное состояние виджета, в случае если пользователь не авторизован
const setInitialDeliveryBasket = () => {
  if (isAuth.value) return

  const deliveryWidget = storage.get<SetDeliveryMethodPayload>(
    STORAGE_KEY.addressSystem.deliveryWidget,
  )

  if (!deliveryWidget) return

  addressSystemStore.changeDeliveryBasket(deliveryWidget)
  addressSystemStore.changeDeliveryWidget(deliveryWidget)
}

onMounted(() => setInitialDeliveryBasket())
</script>
<template>
  <div
    class="widget-delivery"
    :class="{ 'widget-delivery--hidden': isBasketSecondStep }"
    :aria-disabled="isBasketSecondStep"
    @click="openDialogAddress"
  >
    <the-tooltip class="widget-delivery__tooltip" fixed with-icon>
      <template #activator>
        <the-loader v-if="isLoadingDeliveryWidget" color="var(--ui-primary)" />

        <div v-else class="widget-delivery__container">
          <div class="widget-delivery__type-delivery">
            <the-icon
              :name="currentDeliveryWidget.icon"
              :color="currentDeliveryWidget.color"
            />
          </div>

          <div
            :class="{ 'widget-delivery__address--not-address': isAddressUnset }"
            class="widget-delivery__address"
          >
            {{ currentBasketAddress || TITLE_NO_ADDRESS }}
          </div>

          <the-icon name="expand-right" color="var(--ui-secondary)" />
        </div>
      </template>

      <template v-if="isBasketSecondStep" #default>
        <span class="widget-delivery__message">
          Выбор адреса возможен только на первом этапе оформления заказа
        </span>
      </template>
    </the-tooltip>
  </div>
</template>

<style lang="scss" scoped>
.widget-delivery {
  width: 320px;
  height: 40px;
  cursor: pointer;
  background-color: var(--ui-grey);
  border-radius: 40px;
  transition: 120ms all ease-in-out;

  &__container {
    position: relative;
    display: flex;
    align-items: center;
    width: 100%;
    padding: 8px 8px 8px 12px;
  }

  @include media('<=tablet') {
    width: 100%;
  }
  &:hover {
    background-color: var(--ui-grey--hover);
    transition: 150ms all ease-in-out;
  }

  &__type-delivery {
    margin-right: 8px;
  }

  &__address {
    width: inherit;
    overflow: hidden;
    font-size: 14px;
    font-weight: 600;
    line-height: 14px;
    text-overflow: ellipsis;
    white-space: nowrap;
    -webkit-line-clamp: 2;
    user-select: none;

    &--not-address {
      color: var(--ui-dark-grey);
    }
  }

  &__tooltip {
    display: block !important;
    height: 100%;
  }

  &__message {
    display: block;
    font-size: 12px;
    font-weight: 600;
    line-height: 16px;
  }

  &--hidden {
    cursor: default;
    opacity: 0.5;
  }
}
</style>
